import type {
  MessageDecoder,
  MessageEncoder,
  TrackReferenceOrPlaceholder,
  WidgetState,
} from '@livekit/components-core';
import { isEqualTrackRef, isTrackReference, isWeb, log } from '@livekit/components-core';
import { RoomEvent, Track } from 'livekit-client';
import * as React from 'react';
import {
  CarouselLayout,
  ConnectionStateToast,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  RoomAudioRenderer,
  useCreateLayoutContext,
  usePinnedTracks,
  useTracks,
  Chat,
  MessageFormatter,
  useRoomContext
} from '@livekit/components-react';
import { useSearchParams } from 'next/navigation';
import { ParticipantsDrawer } from './ParticipantsDrawer';
import { CustomParticipantTile } from './CustomParticipantTile';
import { ControlBar } from './ControlBar';
import { AuthDrawer } from './AuthDrawer';
import {
  parseHandRaisedAt,
  parseParticipantOverride,
  parseRoomPolicy,
  resolveMediaLock,
  type MediaLockReason,
  type RoomMediaPolicy,
} from './mediaPolicy';
import { parseLobby, pendingLobbyEntries } from './lobby';

/**
 * Why a control is locked, in words. Kept in one place because the same reason
 * codes are rendered as a tooltip here and as a banner on mobile, and the two
 * drifting apart is how a room-wide mute ends up reading as a personal one.
 */
const lockMessage = (kind: string, reason: MediaLockReason): string => {
  switch (reason) {
    case 'restricted':
      return `${kind} is off for you. Only a moderator can turn it on — raise your hand to ask.`;
    case 'muted_all':
      return `${kind} is off for everyone in this meeting.`;
    case 'disabled':
      return `${kind} is disabled. A moderator has turned it off.`;
    case 'server_muted':
      return `${kind} is disabled. A moderator has muted you.`;
    default:
      return '';
  }
};

/**
 * @public
 */
export interface VideoConferenceProps extends React.HTMLAttributes<HTMLDivElement> {
  chatMessageFormatter?: MessageFormatter;
  chatMessageEncoder?: MessageEncoder;
  chatMessageDecoder?: MessageDecoder;
  /** @alpha */
  SettingsComponent?: React.ComponentType;
  viewer?: boolean
  userRole?: string
  /**
   * Read-only page permission, or admitted off a shared link. Every device
   * starts locked and only a moderator can hand one back.
   */
  restricted?: boolean
}

/**
 * The `VideoConference` ready-made component is your drop-in solution for a classic video conferencing application.
 * It provides functionality such as focusing on one participant, grid view with pagination to handle large numbers
 * of participants, basic non-persistent chat, screen sharing, and more.
 *
 * @remarks
 * The component is implemented with other LiveKit components like `FocusContextProvider`,
 * `GridLayout`, `ControlBar`, `FocusLayoutContainer` and `FocusLayout`.
 * You can use these components as a starting point for your own custom video conferencing application.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <VideoConference />
 * <LiveKitRoom>
 * ```
 * @public
 */
export function CustomVideoConference({
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  SettingsComponent,
  viewer,
  userRole,
  restricted,
  ...props
}: VideoConferenceProps) {
  const searchParams = useSearchParams();
  // const canMute = searchParams?.get('canMute') === 'true';
  const canMute = userRole === 'edit';
  const room = useRoomContext();

  const [widgetState, setWidgetState] = React.useState<WidgetState & { showParticipants?: boolean; showAuthDrawer?: boolean }>({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
    showParticipants: false,
    showAuthDrawer: false,
  });
  const lastAutoFocusedScreenShareTrack = React.useRef<TrackReferenceOrPlaceholder | null>(null);

  // Lock state is no longer stored directly — it's derived from three inputs by
  // resolveMediaLock, so the precedence between them lives in exactly one place:
  //
  //   serverMuted* → ephemeral, from a MICROPHONE_CONTROL / CAMERA_CONTROL packet
  //   individual*  → tri-state, from a PARTICIPANT_ENABLE packet or our own
  //                  participant metadata (undefined = follow the room)
  //   roomPolicy   → room-wide mute, from room metadata
  const [serverMutedMic, setServerMutedMic] = React.useState(false);
  const [serverMutedCamera, setServerMutedCamera] = React.useState(false);
  const [individualMic, setIndividualMic] = React.useState<boolean | undefined>(undefined);
  const [individualCamera, setIndividualCamera] = React.useState<boolean | undefined>(undefined);
  const [individualScreenShare, setIndividualScreenShare] = React.useState<boolean | undefined>(
    undefined,
  );
  const [roomPolicy, setRoomPolicy] = React.useState<RoomMediaPolicy>({});
  // Badges the Participants button so a moderator with the drawer shut still
  // notices somebody standing at the door.
  const [waitingCount, setWaitingCount] = React.useState(0);
  const [lastDataMessageSequence, setLastDataMessageSequence] = React.useState<number>(0);
  const [lastCameraDataMessageSequence, setLastCameraDataMessageSequence] = React.useState<number>(0);
  const userInitiatedMuteRef = React.useRef<boolean>(false);
  const userInitiatedCameraRef = React.useRef<boolean>(false);
  const [handRaisedAt, setHandRaisedAt] = React.useState<number | undefined>(undefined);
  const [raisedHandCount, setRaisedHandCount] = React.useState(0);
  const [raisingHand, setRaisingHand] = React.useState(false);

  const micLock = resolveMediaLock({
    individual: individualMic,
    roomWide: roomPolicy.muteAll,
    isModerator: canMute,
    serverMuted: serverMutedMic,
    restricted,
  });
  const cameraLock = resolveMediaLock({
    individual: individualCamera,
    roomWide: roomPolicy.cameraOffAll,
    isModerator: canMute,
    serverMuted: serverMutedCamera,
    restricted,
  });
  // No room-wide screen-share policy exists, so this is purely the individual
  // grant against the restricted default.
  const screenShareLock = resolveMediaLock({
    individual: individualScreenShare,
    roomWide: undefined,
    isModerator: canMute,
    restricted,
  });
  const isMicrophoneControlDisabled = micLock.locked;
  const microphoneDisabledReason = micLock.reason;
  const isCameraControlDisabled = cameraLock.locked;
  const cameraDisabledReason = cameraLock.reason;
  const isScreenShareControlDisabled = screenShareLock.locked;

  const tracksObj = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );

  const tracks = tracksObj.filter(track => {
    try {
      const metadata = JSON.parse(track.participant.metadata || "{}");
      return metadata.role !== "viewer";
    } catch (e) {
      console.error("Error parsing participant metadata:", e);
      return true;
    }
  });

  const widgetUpdate = React.useCallback((state: WidgetState) => {
    log.debug('updating widget state', state);
    setWidgetState((prev) => ({
      ...prev,
      ...state,
      // Preserve showParticipants and showAuthDrawer if not in the new state
      showParticipants: (state as any).showParticipants ?? prev.showParticipants,
      showAuthDrawer: (state as any).showAuthDrawer ?? prev.showAuthDrawer,
    }));
  }, []);

  // Listen for data messages to disable/enable microphone and camera control
  React.useEffect(() => {
    const handleDataReceived = (payload: Uint8Array, participant: any, kind: any, topic: string | undefined) => {
      try {
        const decoder = new TextDecoder();
        const messageText = decoder.decode(payload);
        const message = JSON.parse(messageText);

        console.log('data message received', message);

        if (message.type === 'MICROPHONE_CONTROL' && message.payload) {
          const { disabled, reason, timestamp, sequence } = message.payload;

          // Deduplicate messages using sequence number
          if (sequence && sequence <= lastDataMessageSequence) {
            console.log('Ignoring duplicate data message:', sequence);
            return;
          }

          if (sequence) {
            setLastDataMessageSequence(sequence);
          }

          setServerMutedMic(disabled);

          console.log('Microphone control state updated from data message:', {
            disabled,
            reason,
            sequence,
          });
        } else if (message.type === 'CAMERA_CONTROL' && message.payload) {
          const { disabled, reason, timestamp, sequence } = message.payload;

          // Deduplicate messages using sequence number
          if (sequence && sequence <= lastCameraDataMessageSequence) {
            console.log('Ignoring duplicate camera data message:', sequence);
            return;
          }

          if (sequence) {
            setLastCameraDataMessageSequence(sequence);
          }

          setServerMutedCamera(disabled);

          console.log('Camera control state updated from data message:', {
            disabled,
            reason,
            sequence,
          });
        } else if (message.type === 'PARTICIPANT_ENABLE' && message.payload) {
          const { participantSid, trackType, enabled, timestamp, sequence } = message.payload;

          // Only handle if this message is for the local participant
          if (participantSid === room.localParticipant.sid) {
            // Record the explicit override as tri-state and let resolveMediaLock
            // decide. `enabled: true` is an *exemption* that outranks a
            // room-wide mute — it is not the same as "no override set", which
            // is why this can't collapse to a boolean.
            if (trackType === 'microphone') {
              setIndividualMic(enabled);
              // A moderator granting the mic also lifts any server-mute on it;
              // otherwise the grant would be invisible behind a stale packet.
              if (enabled) setServerMutedMic(false);
              console.log(`Microphone ${enabled ? 'enabled' : 'disabled'} via PARTICIPANT_ENABLE`);
            } else if (trackType === 'camera') {
              setIndividualCamera(enabled);
              if (enabled) setServerMutedCamera(false);
              console.log(`Camera ${enabled ? 'enabled' : 'disabled'} via PARTICIPANT_ENABLE`);
            } else if (trackType === 'screenshare') {
              setIndividualScreenShare(enabled);
              console.log(`Screen share ${enabled ? 'enabled' : 'disabled'} via PARTICIPANT_ENABLE`);
            }
          }
        }
      } catch (error) {
        console.error('Error parsing data message:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, lastDataMessageSequence, lastCameraDataMessageSequence]);

  // Room-wide policy. LiveKit ships room metadata in the JoinResponse and
  // re-broadcasts it on change, so this covers both "a moderator flipped the
  // toggle just now" and "the toggle was flipped before I ever joined" — the
  // latter being the case a data packet can never cover, since packets don't
  // replay for someone who wasn't in the room to receive them.
  //
  // The waiting-room queue rides on the same metadata and so gets the same
  // guarantees for free — including the one that matters most here, that a
  // moderator arriving after someone knocked still sees the knock.
  React.useEffect(() => {
    const syncRoomMetadata = () => {
      setRoomPolicy(parseRoomPolicy(room.metadata));
      setWaitingCount(pendingLobbyEntries(parseLobby(room.metadata)).length);
    };
    syncRoomMetadata();
    room.on(RoomEvent.RoomMetadataChanged, syncRoomMetadata);
    room.on(RoomEvent.Connected, syncRoomMetadata);
    return () => {
      room.off(RoomEvent.RoomMetadataChanged, syncRoomMetadata);
      room.off(RoomEvent.Connected, syncRoomMetadata);
    };
  }, [room]);

  // Our own individual override, recovered from participant metadata. Covers a
  // dropped packet and a signal reconnect (the session, and its metadata,
  // survive that).
  //
  // It does NOT survive a full rejoin — participant metadata is session-scoped
  // and a fresh join rebuilds it from the token's metadata claim. An exemption
  // granted mid-call is therefore lost if that person fully reconnects, and a
  // room-wide mute covers them again. See the note in mediaPolicy.ts.
  React.useEffect(() => {
    const syncOwnOverride = (_prev?: string, participant?: any) => {
      // Fires for remote participants too; only ours says anything about us.
      if (participant && participant !== room.localParticipant) return;
      const override = parseParticipantOverride(room.localParticipant.metadata);
      setIndividualMic(override.micEnabled);
      setIndividualCamera(override.cameraEnabled);
      setIndividualScreenShare(override.screenShareEnabled);
    };
    syncOwnOverride();
    room.on(RoomEvent.ParticipantMetadataChanged, syncOwnOverride);
    room.on(RoomEvent.Connected, syncOwnOverride);
    return () => {
      room.off(RoomEvent.ParticipantMetadataChanged, syncOwnOverride);
      room.off(RoomEvent.Connected, syncOwnOverride);
    };
  }, [room]);

  // Our own raised hand. Read from attributes rather than held as local UI state
  // so it stays correct when the server lowers it for us — which it does the
  // moment a moderator grants the mic, in the same write as the grant.
  React.useEffect(() => {
    const syncOwnHand = () =>
      setHandRaisedAt(parseHandRaisedAt(room.localParticipant.attributes));
    syncOwnHand();
    const onAttributesChanged = (
      _changed: Record<string, string>,
      participant: any,
    ) => {
      if (participant !== room.localParticipant) return;
      syncOwnHand();
    };
    room.on(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
    room.on(RoomEvent.Connected, syncOwnHand);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
      room.off(RoomEvent.Connected, syncOwnHand);
    };
  }, [room]);

  // Room-wide raised-hand count. Only a moderator needs it (it badges the
  // Participants button), so skip the bookkeeping entirely for everyone else.
  React.useEffect(() => {
    if (!canMute) return;
    const recount = () => {
      let count = 0;
      if (parseHandRaisedAt(room.localParticipant.attributes) !== undefined) count++;
      room.remoteParticipants.forEach((p) => {
        if (parseHandRaisedAt(p.attributes) !== undefined) count++;
      });
      setRaisedHandCount(count);
    };
    recount();
    room.on(RoomEvent.ParticipantAttributesChanged, recount);
    room.on(RoomEvent.ParticipantConnected, recount);
    room.on(RoomEvent.ParticipantDisconnected, recount);
    room.on(RoomEvent.Connected, recount);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, recount);
      room.off(RoomEvent.ParticipantConnected, recount);
      room.off(RoomEvent.ParticipantDisconnected, recount);
      room.off(RoomEvent.Connected, recount);
    };
  }, [room, canMute]);

  const handleRaiseHandToggle = React.useCallback(async () => {
    if (raisingHand) return;
    const sid = room.localParticipant.sid;
    if (!sid) return;
    const next = handRaisedAt === undefined;
    setRaisingHand(true);
    try {
      const res = await fetch(
        `/api/participants/raise-hand?roomName=${encodeURIComponent(room.name)}&participantSid=${encodeURIComponent(sid)}&raised=${next}`,
      );
      if (!res.ok) {
        console.error('Failed to toggle raised hand:', res.status, await res.text());
      }
      // No optimistic update — ParticipantAttributesChanged is the source of
      // truth, so the button can't get stuck showing a hand the server refused.
    } catch (error) {
      console.error('Error toggling raised hand:', error);
    } finally {
      setRaisingHand(false);
    }
  }, [room, handRaisedAt, raisingHand]);

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter((track) => !isEqualTrackRef(track, focusTrack));

  React.useEffect(() => {
    // If screen share tracks are published, and no pin is set explicitly, auto set the screen share.
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug('Auto set screen share focus:', { newScreenShareTrack: screenShareTracks[0] });
      layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: screenShareTracks[0] });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug('Auto clearing screen share focus.');
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: updatedFocusTrack });
      }
    }
  }, [
    screenShareTracks
      .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
      .join(),
    focusTrack?.publication?.trackSid,
    tracks,
  ]);

  // useWarnAboutMissingStyles();

  return (
    <div className="lk-video-conference" {...props}>
      {isWeb() && (
        <LayoutContextProvider
          value={layoutContext}
          // onPinChange={handleFocusStateChange}
          onWidgetChange={widgetUpdate}
        >
          <div className="lk-video-conference-inner">
            {!focusTrack ? (
              <div className="lk-grid-layout-wrapper">
                <GridLayout tracks={tracks}>
                  <CustomParticipantTile />
                </GridLayout>
              </div>
            ) : (
              <div className="lk-focus-layout-wrapper">
                <FocusLayoutContainer>
                  <CarouselLayout tracks={carouselTracks}>
                    <CustomParticipantTile />
                  </CarouselLayout>
                  {focusTrack && <FocusLayout trackRef={focusTrack} />}
                </FocusLayoutContainer>
              </div>
            )}
            {/* The lock tooltips live inside .lk-control-bar, which is
                `overflow: hidden` at <=640px — so on a phone a restricted
                participant would see three dimmed buttons and no explanation
                anywhere. This says it once, in the layout rather than on hover,
                and points at the hand as the way out. Mirrors the banner the
                mobile app shows above its own control bar. */}
            {restricted && !canMute && (
              <div className="lk-restricted-banner" role="status">
                <span aria-hidden="true">🔒</span>
                {handRaisedAt !== undefined
                  ? 'Hand raised — waiting for a moderator to let you speak.'
                  : 'You have view-only access. Raise your hand to ask a moderator to let you speak or share.'}
              </div>
            )}
            <ControlBar
              controls={{
                // These three are stated explicitly rather than left to
                // ControlBar's `??= localPermissions.canPublish` default. A
                // restricted participant's token says canPublish: false, which
                // would hide all three outright — and a button that isn't there
                // can't explain itself or show that it might come back.
                microphone: !viewer,
                camera: !viewer,
                screenShare: !viewer,
                chat: !viewer,
                settings: !!SettingsComponent && !viewer,
                participants: true,
                // Moderators are already exempt from a room-wide mute, so
                // raising a hand at themselves would be a no-op. Stated
                // explicitly for the same reason as above: this is the ONE
                // control a restricted participant most needs, since it is how
                // they ask for the devices they've just been refused, and the
                // canPublish default would have hidden it.
                raiseHand: !viewer && !canMute,
              }}
              isHandRaised={handRaisedAt !== undefined}
              onRaiseHandToggle={handleRaiseHandToggle}
              raisedHandCount={raisedHandCount}
              waitingCount={canMute ? waitingCount : 0}
              microphoneDisabled={isMicrophoneControlDisabled}
              microphoneDisabledReason={lockMessage('Microphone', microphoneDisabledReason)}
              onMicrophoneToggle={(enabled: boolean) => {
                // Track user-initiated actions
                userInitiatedMuteRef.current = true;
                // If microphone is disabled by server, prevent unmute
                if (!enabled && isMicrophoneControlDisabled) {
                  console.warn('Attempted to unmute microphone, but it is disabled by server');
                  // Prevent the action - TrackToggle will be disabled, but this is a safety check
                  return false;
                }
                return true;
              }}
              screenShareDisabled={isScreenShareControlDisabled}
              screenShareDisabledReason={lockMessage('Screen sharing', screenShareLock.reason)}
              cameraDisabled={isCameraControlDisabled}
              cameraDisabledReason={lockMessage('Camera', cameraDisabledReason)}
              onCameraToggle={(enabled: boolean) => {
                // Track user-initiated actions
                userInitiatedCameraRef.current = true;
                // If camera is disabled by server, prevent unmute
                if (!enabled && isCameraControlDisabled) {
                  console.warn('Attempted to unmute camera, but it is disabled by server');
                  // Prevent the action - TrackToggle will be disabled, but this is a safety check
                  return false;
                }
                return true;
              }}
              onParticipantsToggle={() => {
                setWidgetState((prev) => ({
                  ...prev,
                  showParticipants: !prev.showParticipants,
                }));
              }}
              isParticipantsOpen={widgetState.showParticipants}
              onAuthDrawerToggle={() => {
                setWidgetState((prev) => ({
                  ...prev,
                  showAuthDrawer: !prev.showAuthDrawer,
                }));
              }}
              isAuthDrawerOpen={widgetState.showAuthDrawer}
            />
          </div>
          <Chat
            style={{ display: widgetState.showChat ? 'grid' : 'none' }}
            messageFormatter={chatMessageFormatter}
            messageEncoder={chatMessageEncoder}
            messageDecoder={chatMessageDecoder}
          />
          {SettingsComponent && !viewer && (
            <div
              className="lk-settings-menu-modal"
              style={{ display: widgetState.showSettings ? 'block' : 'none' }}
            >
              <SettingsComponent />
            </div>
          )}
          <ParticipantsDrawer
            canMute={canMute}
            // canMute={true}
            onClose={() => {
              setWidgetState((prev) => ({
                ...prev,
                showParticipants: false,
              }));
            }}
            style={{ display: widgetState.showParticipants ? 'block' : 'none' }}
          />
          <AuthDrawer
            onClose={() => {
              setWidgetState((prev) => ({
                ...prev,
                showAuthDrawer: false,
              }));
            }}
            style={{ display: widgetState.showAuthDrawer ? 'block' : 'none' }}
          />
        </LayoutContextProvider>
      )}
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
